'use client';

import { faMessageLines, faPen, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@pickleballinc/ui/components/ui/tooltip';
import dayjs from 'dayjs';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';

import { SmallActionButton, PrintButton } from '@/components/atoms';
import { EditTeamSettingsForm, InviteOrResendCodeModal, SendTextForm } from '@/components/forms';
import { GroupPlayers } from '@/components/forms/modals/SendTextForm';
import { TeamPlayersTable } from '@/components/molecules';
import { TeamRosterPrint } from '@/components/organisms/TeamRosterPrint';
import { EditTeamSettingsData, SendTextData } from '@/entities';
import { Result } from '@/entities';
import { InvitationCodeData } from '@/entities/InvitationCodeData';
import { PermissionRole } from '@/entities/PermissionRoles';
import { SeasonListItem } from '@/entities/SeasonList';
import { Currency } from '@/entities/shared/lookups/Currency';
import { currencyFormats } from '@/entities/shared/lookups/CurrencyFormat';
import { ApproveTransfers, TeamLeagueInfo } from '@/entities/TeamLeagueInfo';
import { ActivityPlayerInfoData, TeamPlayerInfo } from '@/entities/TeamPlayerInfo';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { useCreateInvitationCodeMutate, useSendMessageMutate, useUpdateInvitationCodeMutate, useUpdateIsFreeAgentMutate } from '@/hooks/hooks';
import { publicEnv } from '@/lib/env';
import { useOrganizationStore } from '@/store/organization-store';
import { usePermissionStore } from '@/store/permission-store';
import { determinePriceDisplay } from '@/utils/utils';

interface TeamInfoDataProps {
	teamLeagueId: string;
	teamId: string;
	name: string;
	players: TeamPlayerInfo[] | ActivityPlayerInfoData[];
	seasonId: string;
	isFreeAgents?: boolean;
	teamPaysRegistration: boolean | null;
	hasCaptainPaid: boolean | null;
	balance: string;
	cartUuid: string | null;
	leagueInfo: TeamLeagueInfo | undefined;
	currencies: Result<Currency[]> | undefined;
	invitationCode?: InvitationCodeData | null;
	seasonData: SeasonListItem | null;
}

interface SendMessagePayload {
	users_uuid: string[];
	message: string;
}

function isDateBeforeToday(dateString: string | undefined | null): boolean {
	return dateString ? dayjs(dateString).isBefore(dayjs(), 'day') : false;
}

function isDateDisabled(dateDisabled: string | undefined | null): boolean {
	if (!dateDisabled) {
		return false;
	}
	return dateDisabled !== '0001-01-01T00:00:00Z';
}

export const TeamInfoData = ({
	name,
	players,
	teamLeagueId,
	teamId,
	seasonId,
	isFreeAgents = false,
	leagueInfo,
	currencies,
	teamPaysRegistration,
	hasCaptainPaid,
	balance,
	cartUuid,
	invitationCode,
	seasonData
}: TeamInfoDataProps) => {
	const path = usePathname();
	const isActiveTeams = path.includes('active-teams');

	const store = useOrganizationStore();
	const permissionStore = usePermissionStore();
	const teamLeagueInfo = useTeamLeagueInfo();

	const shouldHidePlayerMoveButton =
		teamLeagueInfo.autoApproveTransfers === ApproveTransfers.TRANSFER_APPROVAL_ONLY_COMMISSIONER_CAN_INITIATE &&
		permissionStore.userPermission !== PermissionRole.SUPER_ADMIN &&
		permissionStore.userPermission !== PermissionRole.TEAM_LEAGUES_GLOBAL_MANAGER &&
		permissionStore.userPermission !== PermissionRole.COMMISSIONER &&
		permissionStore.userPermission !== PermissionRole.PRIMARY_MANAGER;

	const [playersList, setPlayersList] = useState<GroupPlayers[]>([]);
	const [isInvitationCodeModalOpen, setIsInvitationCodeModalOpen] = useState(false);
	const [isInviteOrResendCodeModalOpen, setIsInviteOrResendCodeModalOpen] = useState(false);
	const [isSendMessageModalOpen, setIsSendMessageModalOpen] = useState(false);

	const [playerId, setPlayerId] = useState('');
	const [sendMessagModalTitle, setSendMessagModalTitle] = useState('');
	const [modalTitle, setModalTitle] = useState('');

	const printRef = useRef<HTMLDivElement>(null);
	const handlePrint = useReactToPrint({
		contentRef: printRef,
		documentTitle: `${name}_Roster_${new Date().toLocaleDateString('en-CA').replace(/-/g, '')}.pdf`
	});

	const currency = currencies?.data?.find((currency: Currency) => currency.id === leagueInfo?.currencyId.toString());

	const { mutateAsync: updateIsFreeAgent } = useUpdateIsFreeAgentMutate();
	const { mutateAsync: updateInvitationCode } = useUpdateInvitationCodeMutate();
	const { mutateAsync: createInvitationCode } = useCreateInvitationCodeMutate();
	const { mutateAsync: sendMessage } = useSendMessageMutate();

	const handleModalOpen = (title: string) => {
		setModalTitle(title);
		setIsInviteOrResendCodeModalOpen(true);
	};

	const sendMessageSubmit = async (data: SendTextData) => {
		const requestPayload: SendMessagePayload = {
			users_uuid: playerId ? [playerId] : Object.keys(data.players).filter((id) => data.players[id]),
			message: data.message
		};
		if (requestPayload.users_uuid.length === 0) return;

		await sendMessage({ teamLeagueUuid: store.teamLeagueUuid, organizationUuid: store.organizationUuid, teamUuid: teamId, requestPayload });
		setIsSendMessageModalOpen(false);
	};

	const handleSendMessageModalOpen = (title: string, groupOfPlayers: boolean, playerId?: string) => {
		setSendMessagModalTitle(title);

		setPlayersList(
			groupOfPlayers
				? players.map((player, index) => ({
						uuid: `${player.uuid}-${index}`,
						id: player.uuid,
						name: player.fullname,
						isDisabled: !player.sendTextAlerts
					}))
				: []
		);

		setPlayerId(playerId || '');
		setIsSendMessageModalOpen(true);
	};

	const handleInvitationCodeSubmit = async (data: EditTeamSettingsData) => {
		if ((data.isFreeAgent ?? false) !== (isFreeAgents ?? false)) {
			const requestPayload = {
				allow_free_agent_requests: data.isFreeAgent
			};

			await updateIsFreeAgent({
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				teamUuid: teamId,
				requestPayload
			});
		}

		if (data.invitationCode && (!invitationCode?.code || isDateBeforeToday(invitationCode?.dateOfExpiry))) {
			const requestPayload = {
				code: data.invitationCode,
				date_of_expiry: data.expirationDate,
				number_of_uses: data.numberOfTimesCodeCanBeUsed ? data.numberOfTimesCodeCanBeUsed : null
			};

			await createInvitationCode({
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				teamUuid: teamId,
				requestPayload
			});

			setIsInvitationCodeModalOpen(false);
			return;
		}

		if (
			data.invitationCode &&
			invitationCode?.uuid &&
			(!isDateDisabled(invitationCode?.dateDisabled) !== data.isActive ||
				invitationCode?.dateOfExpiry !== data.expirationDate ||
				invitationCode?.numberOfUses !== (data.numberOfTimesCodeCanBeUsed == '' ? null : data.numberOfTimesCodeCanBeUsed) ||
				invitationCode?.code !== data.invitationCode)
		) {
			const dateDisabled = !data.isActive ? dayjs().toISOString() : null;

			const requestPayload = {
				uuid: invitationCode?.uuid,
				code: data.invitationCode,
				date_of_expiry: data.expirationDate,
				number_of_uses: data.numberOfTimesCodeCanBeUsed ? data.numberOfTimesCodeCanBeUsed : null,
				date_disabled: dateDisabled
			};

			await updateInvitationCode({
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				teamUuid: teamId,
				codeUuid: invitationCode?.uuid,
				requestPayload
			});
		}

		setIsInvitationCodeModalOpen(false);
	};

	const currencyAbbr = leagueInfo && currency?.abbreviation ? currencyFormats[currency.abbreviation] : null;

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center gap-4">
				<span className="text-lg font-bold lg:text-xl">{name}</span>
				<FontAwesomeIcon onClick={() => setIsInvitationCodeModalOpen(true)} className="cursor-pointer" icon={faPen} />
			</div>

			<div className="flex flex-col gap-4 sm:flex-row">
				{!shouldHidePlayerMoveButton && (
					<Link
						className="flex h-[26px] items-center justify-center gap-1 rounded border-darkorange-100 bg-darkorange-50 px-2 py-1 text-xs font-semibold leading-[18px] text-darkorange-400 "
						href={`/team-leagues/${teamLeagueId}/teams/${teamId}/player-moves`}
						prefetch={false}
					>
						<FontAwesomeIcon icon={faPlus} />
						Add or move player
					</Link>
				)}

				{isActiveTeams ? (
					<SmallActionButton prefixIcon={faMessageLines} onClick={() => handleSendMessageModalOpen(name, true)}>
						Send text
					</SmallActionButton>
				) : null}

				<div className="flex gap-4">
					{!invitationCode?.code ? (
						<TooltipProvider delayDuration={0} skipDelayDuration={0}>
							<Tooltip>
								<TooltipTrigger asChild>
									<SmallActionButton>Invite with code</SmallActionButton>
								</TooltipTrigger>
								<TooltipPortal>
									<TooltipContent className="bg-gray-900">
										<p>This team doesn`t have invite code.</p>
										<p>Please set it up to be able to invite people.</p>
									</TooltipContent>
								</TooltipPortal>
							</Tooltip>
						</TooltipProvider>
					) : (
						<SmallActionButton onClick={() => handleModalOpen('Invite with code')}>Invite with code</SmallActionButton>
					)}

					{/* <SmallActionButton suffixIcon={faQrcode}>Invite with QR code</SmallActionButton> */}
				</div>

				{!!isActiveTeams &&
					permissionStore.userPermission === PermissionRole.TEAM_CAPTAIN &&
					teamPaysRegistration === true &&
					hasCaptainPaid === false && (
						<Link
							href={`${publicEnv.NEXT_PUBLIC_PTL_CHECKOUT_URL}/team-league/${store.organizationUuid}/${store.teamLeagueUuid}/${seasonId}/${cartUuid}`}
						>
							<SmallActionButton className="!bg-[#D92D20] !text-white" onClick={() => {}}>
								{`Balance due: ${currencyAbbr ? determinePriceDisplay(Number(balance) / 100, currencyAbbr) : (Number(balance) / 100)?.toFixed(2)}`}
							</SmallActionButton>
						</Link>
					)}
				{!!isActiveTeams && <PrintButton label="Print Roster" className="ml-auto px-3 py-1" onClick={handlePrint} />}
			</div>

			<div>
				<div>
					<TeamPlayersTable
						teamData={{ name, players, teamId }}
						handleModalOpen={handleModalOpen}
						handleSendMessageModalOpen={handleSendMessageModalOpen}
						teamLeagueId={teamLeagueId}
						seasonId={seasonId}
						currency={currencyAbbr}
						seasonData={seasonData}
					/>
				</div>
			</div>
			{isInvitationCodeModalOpen ? (
				<EditTeamSettingsForm
					title="Edit codes & requests"
					onClose={() => setIsInvitationCodeModalOpen(false)}
					onSubmit={handleInvitationCodeSubmit}
					currentCode={invitationCode?.code}
					allowFreeAgentRequests={isFreeAgents}
					numberOfUses={invitationCode?.numberOfUses}
					dateOfExpiry={invitationCode?.dateOfExpiry}
					dateDisabled={invitationCode?.dateDisabled}
					isActiveCode={!isDateDisabled(invitationCode?.dateDisabled)}
					codeUuid={invitationCode?.uuid}
					teamUuid={invitationCode?.teamUuid}
					teamName={name}
				/>
			) : null}
			{isInviteOrResendCodeModalOpen ? (
				<InviteOrResendCodeModal
					title={modalTitle}
					invitationCode={invitationCode?.code}
					onClose={() => setIsInviteOrResendCodeModalOpen(false)}
					leagueTitle={leagueInfo?.title || ''}
					seasonUuid={seasonId}
				/>
			) : null}

			{isSendMessageModalOpen ? (
				<SendTextForm
					title={`Send message to ${sendMessagModalTitle}`}
					players={playersList}
					onClose={() => setIsSendMessageModalOpen(false)}
					onSubmit={sendMessageSubmit}
				/>
			) : null}

			{/* Print Component - Hidden on screen, visible when printing only on active teams */}
			{!!isActiveTeams && (
				<div className="hidden">
					<TeamRosterPrint
						ref={printRef}
						teamName={name}
						leagueInfo={{
							seasonTitle: leagueInfo?.seasonTitle || '',
							title: leagueInfo?.title
						}}
						players={players}
					/>
				</div>
			)}
		</div>
	);
};
